// Market Probabilities page — single tab strip with two modes:
//
// Prediction Market: [Month Year]
//   Current month's OT ladder — full cards with Open/High/Low tiles and
//   HIT indicators for triggered levels (live prediction market contracts).
//
// OTC Market: [Jun] [Jul] [Aug] [Sep] [Dec]
//   Forward-month OT pricing — simplified cards (no MTD tiles, no HIT
//   indicators); selecting a month tab re-prices the board at that expiry.
//
// Only one set of cards is shown at a time. A single MarketProbabilitiesProvider
// drives whichever section is active.
import { useContext, useMemo, useState, type ReactNode } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import {
  type FeedStatus,
  MarketProbabilitiesContext,
  MarketProbabilitiesProvider,
} from '../../providers/market-probabilities-provider';
import EmptyState from '../../components/v2/empty-state';
import MarketLadderCard from './market-ladder-card';
import { boardExpiries, formatOtcTabLabel, MonthEndExpiry } from './ladder';
import { TEXT_MUTED, TEXT_HEADING } from './chrome';
import { useNow } from './use-now';

import './styles.scss';

const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Full month name + year from a MonthEndExpiry iso string ("YYYY-MM-DD HH:MM:SS").
function expiryMonthFull(expiry: MonthEndExpiry): string {
  const monthIndex = parseInt(expiry.iso.slice(5, 7), 10) - 1;
  const year = expiry.iso.slice(0, 4);
  return `${MONTH_FULL[monthIndex]} ${year}`;
}

type Selection = { kind: 'prediction'; expiry: string } | { kind: 'otc'; expiry: string };

// RESPONSIVE token-card grid: reflow the cards-per-row down as the viewport
// narrows (4 -> 3 -> 2 -> 1) so a card never gets narrower than the width its
// five inner columns (Level · vs Spot · IMM(BS) · IMM(Local Vol) · IMM(Stoch Vol))
// need to render without overlap. That no-overlap floor is ~360px per card
// (driven by the two "IMM (Local Vol/Stoch Vol)" headers, ~78px each, plus the
// three left tracks and the 14px row padding). The breakpoints below are picked
// so the resulting 1fr card width stays >= ~360px in every regime:
//   >= xl (1536)  4-up  -> card ~362px at 1536, stretching wider on widescreen
//   lg  (1200)    3-up  -> card ~376px at 1200
//   md  (900)     2-up  -> card ~421px at 900
//   < md (0-900)  1-up
// A pure auto-fill/auto-fit minmax(360px,1fr) is deliberately NOT used: it would
// go 5-/6-up on an ultrawide monitor, breaking the "keep widescreen 4-up" look --
// the explicit cap at 4 preserves it while still reflowing down. 1fr tracks fill
// the row exactly, so there is no horizontal page scroll at any width.
const CARD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
    xl: 'repeat(4, 1fr)',
  },
  gap: '14px',
  alignItems: 'start',
};

const TAB_ACTIVE_BG = 'rgba(40, 114, 226, 0.10)';
const TAB_ACTIVE_BORDER = '#026aa2';
const TAB_ACTIVE_TEXT = '#eaf6ff';
const TAB_BORDER = '#1f3645';
// Selected tabs read as a filled accent BUTTON (not a thin input-style border
// box); unselected tabs get a subtle outline so they read as buttons too.
const TAB_ACTIVE_FILL = '#026aa2';
const TAB_IDLE_BORDER = '#2a4152';
const TAB_HOVER_BG = 'rgba(2, 106, 162, 0.14)';

const MarketProbabilitiesPage = () => {
  const allExpiries = useMemo(() => boardExpiries(), []);
  const predictionExpiry = allExpiries[0]; // current month-end

  // Year-end prediction market: the December expiry in the board set. If the
  // current month IS December they're the same contract — we show only one tab.
  const yearEndExpiry = allExpiries.find((e) => e.iso.slice(5, 7) === '12') ?? null;
  const hasYearEnd = yearEndExpiry !== null && yearEndExpiry.iso !== predictionExpiry.iso;

  const [selection, setSelection] = useState<Selection>({
    kind: 'prediction',
    expiry: predictionExpiry.iso,
  });

  const activeExpiry = selection.expiry;
  const activeVariant = selection.kind === 'prediction' ? 'prediction' : 'otc';
  // Prediction tabs read the month_end family; OTC tabs read the Deribit-listed
  // exchange family.
  const activeFamily = selection.kind === 'otc' ? 'exchange' : 'month_end';

  return (
    <Box className="market-probabilities-container" sx={{ pt: 8 }}>
      <MarketProbabilitiesProvider expiry={activeExpiry} family={activeFamily}>
        <Box sx={{ px: '22px', pt: 2, pb: 3 }}>
          {/* Page head — vertically centred so the title, model chip and
              settlement toggles share one centre line. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.625}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <Typography sx={{ color: TEXT_HEADING, fontWeight: 700, fontSize: 18 }}>
              Market Probabilities
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, fontSize: 11.5 }}>
              One-Touch ladders — risk-neutral touch probability under model
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box className="market-probabilities-chip">
              Model&nbsp; Black-Scholes + Local Vol + Stoch Vol
            </Box>
            {/* Settlement variant toggles — locked to the feed's current values
                (IM-1956), flush right opposite the expiry tabs. */}
            <SettlementToggles />
          </Stack>

          {/* Compound tab strip. no-wrap so the OTC group (many exchange expiries)
              stays on one row and scrolls horizontally inside its own container
              rather than wrapping the whole strip to several lines. */}
          <Stack
            direction="row"
            alignItems="center"
            role="tablist"
            sx={{
              mb: 2,
              borderBottom: `1px solid ${TAB_BORDER}`,
              flexWrap: 'nowrap',
              gap: 0.5,
              py: 0.75,
              maxWidth: '100%',
            }}
          >
            {/* Prediction Market label */}
            <Typography
              sx={{
                fontSize: 10,
                color: TEXT_MUTED,
                fontWeight: 600,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                mr: 0.25,
                flexShrink: 0,
              }}
            >
              Prediction Market
            </Typography>

            {/* Current month tab */}
            <TabChip
              active={selection.kind === 'prediction' && selection.expiry === predictionExpiry.iso}
              onClick={() => setSelection({ kind: 'prediction', expiry: predictionExpiry.iso })}
            >
              <Typography component="span" sx={{ fontSize: 11, color: 'inherit' }}>
                {expiryMonthFull(predictionExpiry)}
              </Typography>
            </TabChip>

            {/* Current year tab — only when year-end differs from current month */}
            {hasYearEnd && yearEndExpiry && (
              <TabChip
                active={selection.kind === 'prediction' && selection.expiry === yearEndExpiry.iso}
                onClick={() => setSelection({ kind: 'prediction', expiry: yearEndExpiry.iso })}
              >
                <Typography component="span" sx={{ fontSize: 11, color: 'inherit' }}>
                  Annual {yearEndExpiry.iso.slice(0, 4)}
                </Typography>
              </TabChip>
            )}

            {/* Divider */}
            <Box
              sx={{
                width: '1px',
                height: 18,
                backgroundColor: TAB_BORDER,
                mx: 0.75,
                flexShrink: 0,
              }}
            />

            {/* OTC Market label */}
            <Typography
              sx={{
                fontSize: 10,
                color: TEXT_MUTED,
                fontWeight: 600,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                mr: 0.25,
                flexShrink: 0,
              }}
            >
              OTC Market
            </Typography>

            {/* OTC (exchange-family) tabs — every expiry the feed serves, in a
                horizontally-scrollable row that grows into the remaining width. */}
            <OtcTabs selection={selection} setSelection={setSelection} />
          </Stack>

          {/* Card grid -- or a calm empty-state when the stream is unavailable
              on this environment (no backend feed yet). */}
          <Board
            variant={activeVariant}
            isYearEnd={
              hasYearEnd &&
              yearEndExpiry !== null &&
              selection.expiry === yearEndExpiry.iso
            }
          />
        </Box>
      </MarketProbabilitiesProvider>
    </Box>
  );
};

// Reusable expiry tab — a real <button> that reads unambiguously as a button:
//   unselected -> subtle outline chip (so it's clearly selectable, not plain text)
//   selected   -> filled accent chip (NOT a thin input-style border box; no
//                 text-field / caret look)
// Shared by BOTH the prediction and OTC groups so they match.
const TabChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      borderRadius: '4px',
      border: `1px solid ${active ? TAB_ACTIVE_BORDER : TAB_IDLE_BORDER}`,
      backgroundColor: active ? TAB_ACTIVE_FILL : 'transparent',
      color: active ? TAB_ACTIVE_TEXT : 'text.secondary',
      fontWeight: active ? 600 : 500,
      // Button reset so it never reads as an editable text field.
      fontFamily: 'inherit',
      appearance: 'none',
      outline: 'none',
      userSelect: 'none',
      cursor: 'pointer',
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
      transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
      '&:hover': active
        ? {}
        : { borderColor: TAB_ACTIVE_BORDER, color: 'text.primary', backgroundColor: TAB_HOVER_BG },
      // Keyboard focus: a button-style ring (not an input box-shadow).
      '&:focus-visible': { outline: `2px solid ${TAB_ACTIVE_BORDER}`, outlineOffset: '1px' },
    }}
  >
    {children}
  </Box>
);

// Thin horizontally-scrollable container for the OTC tab row, so a long
// exchange-expiry ladder scrolls instead of overflowing / wrapping the header.
const OTC_SCROLL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flex: '1 1 0',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  // No vertical padding: with no horizontal scrollbar (the common widescreen case)
  // the OTC tabs then share the prediction tabs' centre line exactly.
  py: 0,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: 5 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: TAB_BORDER, borderRadius: 3 },
} as const;

// OTC (exchange-family) expiry tabs, sourced DYNAMICALLY from the feed
// (exchangeExpiries) — every expiry the backend serves, no hard-coded set. Labels
// are UTC dates (formatOtcTabLabel). Empty until the first feed lands.
const OtcTabs = ({
  selection,
  setSelection,
}: {
  selection: Selection;
  setSelection: (s: Selection) => void;
}) => {
  const { exchangeExpiries, feedStatus } = useContext(MarketProbabilitiesContext);
  if (exchangeExpiries.length === 0) {
    // While connecting, hint that the strip is warming; once the feed is confirmed
    // unavailable stay silent -- the board's empty-state carries the message, a
    // lingering "loading…" would contradict it.
    return (
      <Box sx={OTC_SCROLL_SX}>
        {feedStatus !== 'unavailable' && (
          <Typography sx={{ fontSize: 10.5, color: TEXT_MUTED, fontStyle: 'italic', px: 0.5 }}>
            loading…
          </Typography>
        )}
      </Box>
    );
  }
  return (
    <Box sx={OTC_SCROLL_SX}>
      {exchangeExpiries.map((iso) => {
        const isActive = selection.kind === 'otc' && selection.expiry === iso;
        return (
          <TabChip
            key={iso}
            active={isActive}
            onClick={() => setSelection({ kind: 'otc', expiry: iso })}
          >
            <Typography component="span" sx={{ fontSize: 11, color: 'inherit' }}>
              {formatOtcTabLabel(iso)}
            </Typography>
          </TabChip>
        );
      })}
    </Box>
  );
};

// One locked settlement segment control: two labelled segments, the active
// (feed-driven) one highlighted like an active tab, the other muted. Locked =
// non-interactive for now (IM-1956) but clearly visible showing the current value.
const ToggleSeg = ({ active, children }: { active: boolean; children: React.ReactNode }) => (
  <Box
    sx={{
      px: 0.875,
      py: 0.375,
      fontSize: 9.5,
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
      fontWeight: active ? 600 : 500,
      color: active ? TAB_ACTIVE_TEXT : TEXT_MUTED,
      backgroundColor: active ? TAB_ACTIVE_BG : 'transparent',
    }}
  >
    {children}
  </Box>
);

const LockedToggle = ({
  left,
  right,
  leftActive,
}: {
  left: string;
  right: string;
  leftActive: boolean;
}) => (
  <Tooltip
    title="Additional settlement variants coming soon"
    placement="bottom"
    arrow
  >
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '4px',
        border: `1px solid ${TAB_BORDER}`,
        overflow: 'hidden',
        cursor: 'default',
        opacity: 0.92,
        flexShrink: 0,
      }}
    >
      <ToggleSeg active={leftActive}>{left}</ToggleSeg>
      <ToggleSeg active={!leftActive}>{right}</ToggleSeg>
    </Box>
  </Tooltip>
);

// The two locked settlement toggles, driven by the feed's pay_time / pay_token
// (SettlementInfo): pay_at_touch ↔ pay_at_expiry (locked at at_touch) and
// pay_in_base ↔ pay_in_quote (locked at in_quote). Easy to unlock later: wire
// onClick + drop the opacity/cursor lock when IM-1956 ships the 4 variants.
const SettlementToggles = () => {
  const { settlement } = useContext(MarketProbabilitiesContext);
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
      <LockedToggle left="at touch" right="at expiry" leftActive={settlement.onTouch} />
      <LockedToggle left="in base" right="in quote" leftActive={!settlement.inQuote} />
    </Stack>
  );
};

// The board body: the token-card grid, OR a calm full-board empty-state when the
// market-probabilities stream is UNAVAILABLE on this environment (the backend
// feed isn't present yet -- e.g. staging pre-cutover, where subscribe.market_
// probabilities returns -32601). Split into a pure branch (BoardContent) so it's
// unit-testable without the provider / WS stack.
const Board = ({ variant, isYearEnd }: { variant: 'prediction' | 'otc'; isYearEnd?: boolean }) => {
  const { feedStatus } = useContext(MarketProbabilitiesContext);
  return (
    <BoardContent feedStatus={feedStatus}>
      <Box sx={CARD_GRID_SX}>
        <MarketCards variant={variant} isYearEnd={isYearEnd} />
      </Box>
    </BoardContent>
  );
};

// Pure render branch: the calm empty-state on 'unavailable', otherwise the board
// itself. 'connecting' keeps the normal warming affordance (per-card loading +
// the OTC "loading…" hint); only a confirmed-unavailable feed replaces the grid.
export const BoardContent = ({
  feedStatus,
  children,
}: {
  feedStatus: FeedStatus;
  children: ReactNode;
}) => (feedStatus === 'unavailable' ? <AwaitingStream /> : <>{children}</>);

// Shown when the feed backend isn't present on this environment. Reuses the
// shared EmptyState (its muted theme colour -- no hardcoded hue) with a board
// muted-text sub-line. The board repopulates automatically once a push lands
// (feedStatus -> 'live'), so this is a calm holding state, not an error.
const AwaitingStream = () => (
  <Stack alignItems="center" spacing={0.5} sx={{ py: 8, width: '100%' }}>
    <EmptyState variant="no-data" size="lg" message="Awaiting live market-probabilities stream" />
    <Typography sx={{ color: TEXT_MUTED, fontSize: 12.5, textAlign: 'center' }}>
      This board populates automatically once the feed is live on this environment.
    </Typography>
  </Stack>
);

// Renders one card per market for the active section.
const MarketCards = ({ variant, isYearEnd }: { variant: 'prediction' | 'otc'; isYearEnd?: boolean }) => {
  const { markets, expiry } = useContext(MarketProbabilitiesContext);
  const now = useNow();
  return (
    <>
      {markets.map((m) => (
        <MarketLadderCard key={m.token} market={m} expiry={expiry} now={now} variant={variant} useYearAnchor={isYearEnd} />
      ))}
    </>
  );
};

export default MarketProbabilitiesPage;
